import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { X, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  Budget,
  BudgetCategory,
  BudgetItem,
  CreateBudgetingProps,
  EmailOption,
  PremisesData,
  Vendor,
} from "./types/budgeting";
import {
  fetchBudgetById,
  fetchBudgetData,
  fetchPremisesData,
  saveBudget,
  sendBudgetByEmail,
} from "./services/budgetingService";
import {
  createFullAddress,
  getInitialBudget,
  getInitialBudgetCategory,
  getInitialBudgetItem,
  mapBudgetDataFromResponse,
  validateBudget,
} from "./utils/budgetingUtils";
import {
  PremisesInformation,
  BudgetCategoryItem,
  EmailOptions,
} from "./components/budgeting";
import Modal from "../../components/modal/Modal";
import { useS3Upload, deleteS3File } from "../../utils/s3Client";

const CreateBudgeting: React.FC<CreateBudgetingProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isViewMode = mode === "view";
  const isUpdateMode = mode === "update";

  const getModuleName = (): string | undefined => {
    // First check if moduleName was passed via location state
    if (location.state?.moduleName) {
      return location.state.moduleName;
    }

    // Otherwise, check the pathname
    const path = location.pathname;
    if (path.includes("/equipment-management")) {
      return "equipment_management";
    } else if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/risk-assessment")
    ) {
      return "fire_safety";
    }
    return undefined;
  };

  const moduleName = getModuleName();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(true);

  const [premisesData, setPremisesData] = useState<PremisesData[]>([]);
  const [budget, setBudget] = useState<Budget>(() => {
    const initialBudget = getInitialBudget();
    // Pre-render with one category and one item if not in view/update mode
    if (!isViewMode && !isUpdateMode) {
      const initialCategory = getInitialBudgetCategory();
      const initialItem = getInitialBudgetItem();
      initialCategory.items = [initialItem];
      initialBudget.categories = [initialCategory];
    }
    return initialBudget;
  });
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");

  const { uploadFileToS3, progress, uploading, error } = useS3Upload();

  const getFileKey = (value: string): string =>
    value.includes("amazonaws.com/")
      ? decodeURIComponent(value.split("amazonaws.com/")[1])
      : value;

  useEffect(() => {
    const loadPremisesData = async () => {
      try {
        const data = await fetchPremisesData();
        setPremisesData(data);
      } catch (error) {
        console.error("Error fetching premises data:", error);
        setValidationErrors({ premises: true });
      }
    };

    loadPremisesData();
  }, []);

  useEffect(() => {
    if ((isViewMode || isUpdateMode) && id) {
      loadBudgetData();
    }
  }, [id, isViewMode, isUpdateMode]);

  const loadBudgetData = async () => {
    setIsLoading(true);
    try {
      const budgetData = await fetchBudgetById(id!);
      const mappedBudget = mapBudgetDataFromResponse(budgetData);

      if (
        !mappedBudget.emailOptions ||
        mappedBudget.emailOptions.length === 0
      ) {
        mappedBudget.emailOptions = [{ email: "" }];
      }

      setBudget(mappedBudget);
    } catch (err: any) {
      console.error("Error fetching budget data:", err);
      setValidationErrors({ budget: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePremisesChange = (premisesId: string) => {
    if (isViewMode) return;

    const selectedPremises = premisesData.find(
      (premise) => premise.id === premisesId
    );

    if (selectedPremises) {
      const fullAddress = createFullAddress(selectedPremises);

      setBudget((prev) => ({
        ...prev,
        premises: premisesId,
        premisesAddress: fullAddress,
      }));
    }
  };

  const addBudgetCategory = () => {
    if (isViewMode) return;

    const newCategory = getInitialBudgetCategory();
    const initialItem = getInitialBudgetItem();
    newCategory.items = [initialItem];

    setBudget((prevBudget) => ({
      ...prevBudget,
      categories: [...prevBudget.categories, newCategory],
    }));
  };

  const deleteBudgetCategory = (categoryId: string) => {
    if (isViewMode) return;
    setBudget((prevBudget) => ({
      ...prevBudget,
      categories: prevBudget.categories.filter(
        (category) => category.id !== categoryId
      ),
    }));
  };

  const addItemToCategory = (categoryIndex: number) => {
    if (isViewMode) return;

    const newItem = getInitialBudgetItem();

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      const newItems = [...newCategories[categoryIndex].items, newItem];
      newCategories[categoryIndex] = {
        ...newCategories[categoryIndex],
        items: newItems,
      };
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });
  };

  const deleteItemFromCategory = (categoryIndex: number, itemIndex: number) => {
    if (isViewMode) return;

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      const newItems = newCategories[categoryIndex].items.filter(
        (_, idx) => idx !== itemIndex
      );
      newCategories[categoryIndex] = {
        ...newCategories[categoryIndex],
        items: newItems,
      };
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });
  };

  const handleAddEmail = () => {
    if (isViewMode) return;
    setBudget((prevBudget) => ({
      ...prevBudget,
      emailOptions: [...prevBudget.emailOptions, { email: "" }],
    }));
  };

  const handleEmailChange = (index: number, value: string) => {
    if (isViewMode) return;
    setBudget((prevBudget) => {
      const newEmailOptions = [...prevBudget.emailOptions];
      newEmailOptions[index] = { ...newEmailOptions[index], email: value };
      return {
        ...prevBudget,
        emailOptions: newEmailOptions,
      };
    });
  };

  const handleSendEmails = async () => {
    const validEmails = budget.emailOptions.filter(
      (option) =>
        option.email.trim() !== "" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(option.email)
    );
    if (validEmails.length === 0) {
      setValidationErrors({ emailOptions: true });
      return;
    }

    setIsEmailSending(true);
    setValidationErrors({});
    setEmailStatus("");

    try {
      await sendBudgetByEmail(budget);
      setEmailStatus(
        "Budget has been sent successfully to the provided email addresses."
      );
    } catch (err) {
      console.error("Error sending budget by email:", err);
      setValidationErrors({ emailOptions: true });
    } finally {
      setIsEmailSending(false);
    }
  };

  const removeEmail = (index: number) => {
    if (isViewMode) return;
    setBudget((prev) => {
      const newEmailOptions = [...prev.emailOptions];
      newEmailOptions.splice(index, 1);
      return {
        ...prev,
        emailOptions:
          newEmailOptions.length > 0 ? newEmailOptions : [{ email: "" }],
      };
    });
  };

  const handleFileChange = async (
    categoryIndex: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isViewMode) return;

    if (event.target.files) {
      const newFiles = Array.from(event.target.files).filter((file) => {
        if (!file) return false;
        const validTypes = [
          "application/pdf",
          "text/plain",
          "image/jpeg",
          "image/png",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
          console.warn(`Invalid file type: ${file.type}`);
          setValidationErrors((prev) => ({
            ...prev,
            [`file-upload-${categoryIndex}`]: true,
          }));
          return false;
        }
        if (file.size > maxSize) {
          console.warn(`File too large: ${file.name}`);
          setValidationErrors((prev) => ({
            ...prev,
            [`file-upload-${categoryIndex}`]: true,
          }));
          return false;
        }
        return true;
      });

      setBudget((prevBudget) => {
        const newCategories = [...prevBudget.categories];
        newCategories[categoryIndex] = {
          ...newCategories[categoryIndex],
          files: [...(newCategories[categoryIndex].files || []), ...newFiles],
        };
        return {
          ...prevBudget,
          categories: newCategories,
        };
      });

      try {
        const uploadedKeys = await Promise.all(
          newFiles.map((file) =>
            uploadFileToS3(file, { premiseId: budget.premises })
          )
        );

        setBudget((prevBudget) => {
          const newCategories = [...prevBudget.categories];
          const files = [...newCategories[categoryIndex].files];
          let keyIndex = 0;
          for (let i = 0; i < files.length; i++) {
            if (newFiles.includes(files[i] as File)) {
              files[i] = uploadedKeys[keyIndex++];
            }
          }
          newCategories[categoryIndex] = {
            ...newCategories[categoryIndex],
            files,
          };
          return {
            ...prevBudget,
            categories: newCategories,
          };
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        setValidationErrors((prev) => ({
          ...prev,
          [`file-upload-${categoryIndex}`]: true,
        }));
        setStatusMessage("Failed to upload files. Please try again.");
      }
    }
  };

  const removeFile = (categoryIndex: number, fileIndex: number) => {
    if (isViewMode) return;

    const removed = budget.categories[categoryIndex]?.files[fileIndex];

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      newCategories[categoryIndex] = {
        ...newCategories[categoryIndex],
        files: newCategories[categoryIndex].files.filter(
          (_, i) => i !== fileIndex
        ),
      };
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });

    if (typeof removed === "string") {
      deleteS3File(getFileKey(removed)).catch(() => { });
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Navigate back to the correct module path
    // First check if returnPath was passed via location state
    const returnPath = location.state?.returnPath;
    if (returnPath) {
      navigate(returnPath);
    } else if (moduleName === "equipment_management") {
      navigate("/equipment-management/budgeting");
    } else if (moduleName === "hazardous_materials") {
      navigate("/hazardous-materials/budgeting");
    } else {
      navigate("/budgeting");
    }
  };

  const handleSubmit = async () => {
    if (isViewMode) {
      if (isUpdateMode) {
        navigate(`/update-budgeting/${id}`);
      } else {
        handleClose();
      }
      return;
    }

    setValidationErrors({});
    const validationResult = validateBudget(budget);
    if (validationResult) {
      setValidationErrors({ [validationResult.fieldId!]: true });
      const errorElement = document.getElementById(validationResult.fieldId!);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
      return;
    }

    setIsLoading(true);
    try {
      const response = await saveBudget(
        budget,
        id,
        isUpdateMode,
        moduleName
      );
      const updatedBudget = mapBudgetDataFromResponse(response.data);
      setBudget(updatedBudget);

      const validEmails = budget.emailOptions.filter(
        (option) =>
          option.email.trim() !== "" &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(option.email)
      );
      if (validEmails.length > 0) {
        try {
          await sendBudgetByEmail(updatedBudget);
          setStatusMessage(
            "Budget has been saved and sent successfully to the provided email addresses."
          );
          toast.success(
            isUpdateMode
              ? "Budget updated and sent successfully"
              : "Budget created and sent successfully"
          );
        } catch (emailErr) {
          console.error("Error sending budget by email:", emailErr);
          setValidationErrors({ emailOptions: true });
          toast.success(
            isUpdateMode
              ? "Budget updated successfully"
              : "Budget created successfully"
          );
        }
      } else {
        setStatusMessage("Budget has been saved successfully.");
        toast.success(
          isUpdateMode
            ? "Budget updated successfully"
            : "Budget created successfully"
        );
      }
      handleClose();
    } catch (err) {
      console.error("Error saving budget data:", err);
      setValidationErrors({ budget: true });
      toast.error(
        isUpdateMode ? "Failed to update budget" : "Failed to create budget"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateCategoryField = (
    index: number,
    field: keyof BudgetCategory,
    value: string
  ) => {
    if (isViewMode) return;

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      newCategories[index] = {
        ...newCategories[index],
        [field]: value,
      };
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });
  };

  const updateVendorField = (
    categoryIndex: number,
    field: keyof Vendor,
    value: string
  ) => {
    if (isViewMode) return;

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      newCategories[categoryIndex].vendor = {
        ...newCategories[categoryIndex].vendor,
        [field]: value,
      };
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });
  };

  const updateItemField = (
    categoryIndex: number,
    itemIndex: number,
    field: keyof BudgetItem,
    value: string
  ) => {
    if (isViewMode) return;

    setBudget((prevBudget) => {
      const newCategories = [...prevBudget.categories];
      const newItems = [...newCategories[categoryIndex].items];
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        [field]: value,
      };
      newCategories[categoryIndex].items = newItems;
      return {
        ...prevBudget,
        categories: newCategories,
      };
    });
  };

  if (isLoading && (isViewMode || isUpdateMode)) {
    return (
      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        modalClass="bg-white rounded-lg w-full h-full max-w-none max-h-none flex items-center justify-center"
        closeButton={false}
      >
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      modalClass="bg-white rounded-lg w-full h-full max-w-none max-h-none overflow-hidden"
      closeButton={false}
    >
      <div className="shadow-lg rounded-lg w-full h-full bg-[#F8F8FA] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4  bg-[#F8F8FA] rounded-t-lg flex-shrink-0">
          <h1 className="font-bold text-xl text-[#1b428a]">
            {isViewMode ? "View" : isUpdateMode ? "Update" : "Create"} Budgeting
            For Fire Safety Systems
          </h1>
          <button
            className="text-gray-400 hover:text-gray-600 transition-colors"
            onClick={handleClose}
          >
            <X size={24} />
          </button>
        </div>

        {/* Error Messages */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-6 flex items-center flex-shrink-0">
            <AlertCircle size={18} className="mr-2" />
            Please correct the errors in the form.
          </div>
        )}

        {/* Success Messages */}
        {statusMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded m-6 flex-shrink-0">
            {statusMessage}
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <PremisesInformation
            budget={budget}
            setBudget={setBudget}
            premisesData={premisesData}
            isViewMode={isViewMode}
            isUpdateMode={isUpdateMode}
            handlePremisesChange={handlePremisesChange}
            mode={mode}
            validationErrors={validationErrors}
          />

          <div className="p-6 ">
            {budget.categories.map((category, index) => (
              <BudgetCategoryItem
                key={category.id}
                item={category}
                index={index}
                isViewMode={isViewMode}
                isUpdateMode={isUpdateMode}
                updateCategoryField={(field, value) =>
                  updateCategoryField(index, field, value)
                }
                updateVendorField={(field, value) =>
                  updateVendorField(index, field, value)
                }
                updateItemField={(itemIndex, field, value) =>
                  updateItemField(index, itemIndex, field, value)
                }
                addItem={() => addItemToCategory(index)}
                deleteItem={(itemIndex) =>
                  deleteItemFromCategory(index, itemIndex)
                }
                deleteBudgetItem={() => deleteBudgetCategory(category.id)}
                addBudgetItem={addBudgetCategory}
                validationErrors={validationErrors}
                totalCategories={budget.categories.length}
                files={category.files}
                handleFileChange={(e) => handleFileChange(index, e)}
                removeFile={(fileIndex) => removeFile(index, fileIndex)}
                progress={progress}
                error={error}
              />
            ))}
          </div>

          <EmailOptions
            emailOptions={budget.emailOptions}
            handleEmailChange={handleEmailChange}
            handleAddEmail={handleAddEmail}
            removeEmail={removeEmail}
            isViewMode={isViewMode}
          />

          {emailStatus && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded m-6">
              {emailStatus}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-500 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="flex items-center justify-center font-semibold gap-3 bg-gray-400 text-white px-4 py-2 rounded-full"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            disabled={isLoading || uploading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Processing...
              </span>
            ) : isViewMode ? (
              isUpdateMode ? (
                "Edit"
              ) : (
                "Back"
              )
            ) : isUpdateMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateBudgeting;
